import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiConsumer } from '../../service/api-consumer';
import { StatsDataRequest } from '../../interface/request/StatsDataRequest';
import { StatsFetchType } from '../../enum/StatsFetchType';
import { searchStats, StatsResponse } from '../../interface/stats/StatsResponse';
import { AgmsResponseCode } from '../../enum/AgmsResponseCode';

export const fetchStats = createAsyncThunk(
  'data/fetchStats',
  async ({ request, type }: { request: StatsDataRequest; type: StatsFetchType }) => {
    // Only send the actual request data to the API
    const response = await ApiConsumer.getStats(request);
    // Return both API data and type for reducer logic
    return { type, data: response };
  },
);

interface DataState {
  statsData: StatsResponse;
  filteredData: StatsResponse;
  shouldViewPerCurrency: boolean;
  shouldExpandSum: boolean;
  viewPerCurrencyData: StatsResponse;
  availableCurrencies: string[];
  selectedCurrency: string | null;
  loading: boolean;
  error: unknown | null;
  locationColumnHeight: number;
  geoColumnHeight: number;
}

const initialState: DataState = {
  statsData: { Items: [], ResponseCode: AgmsResponseCode.OK, Message: '' },
  filteredData: { Items: [], ResponseCode: AgmsResponseCode.OK, Message: '' },
  viewPerCurrencyData: { Items: [], ResponseCode: AgmsResponseCode.OK, Message: '' },
  availableCurrencies: ['All'],
  selectedCurrency: null,
  shouldViewPerCurrency: false,
  shouldExpandSum: false,
  loading: false,
  error: null,
  locationColumnHeight: 0,
  geoColumnHeight: 0,
};

const statsSlice = createSlice({
  name: 'statsData',
  initialState,
  reducers: {
    applySearchInput(state, action) {
      state.filteredData = searchStats(state.statsData.Items || [], action.payload);
    },
    setShouldViewPerCurrency: (state) => {
      state.shouldViewPerCurrency = !state.shouldViewPerCurrency;
    },
    setShouldExpandSum: (state, { payload }) => {
      state.shouldExpandSum = payload;
    },
    clearCurrencyData: (state) => {
      state.viewPerCurrencyData = {
        Items: [],
        ResponseCode: AgmsResponseCode.OK,
        Message: '',
      };
    },
    setSelectedCurrency: (state, { payload }) => {
      state.selectedCurrency = payload;
    },
    resetTableFooter: (state) => {
      state.shouldViewPerCurrency = false;
      state.shouldExpandSum = false;
      state.viewPerCurrencyData = {
        Items: [],
        ResponseCode: AgmsResponseCode.OK,
        Message: '',
      };
      state.selectedCurrency = '';
    },
    setViewPerCcyDialogHeight: (state, { payload }) => {
      const dialogHeight = Math.ceil((payload.numberOfRecords * payload.rowDensityHeight) / 10) * 10;
      const paddingRowHeight = Math.ceil(dialogHeight / payload.numberOfRecords);

      state.locationColumnHeight = dialogHeight - paddingRowHeight;
      state.geoColumnHeight = dialogHeight;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.loading = false;
        const { type, data } = action.payload;

        if (data.ResponseCode === AgmsResponseCode.OK) {
          if (type === StatsFetchType.VIEW_PER_CURRENCY || type === StatsFetchType.EXPAND_SUM) {
            state.viewPerCurrencyData = data;

            if (state.availableCurrencies.length === 1) {
              state.viewPerCurrencyData.Items.forEach((item) => state.availableCurrencies.push(item.CCY));
            }
          } else {
            state.statsData = data;
            state.filteredData = data;
          }
        } else {
          state.statsData = { ...data, Items: [] };
          state.filteredData = { ...data, Items: [] };
        }
      })
      .addCase(fetchStats.rejected, (state, action) => {
        state.loading = false;
        state.filteredData = { Items: [], ResponseCode: AgmsResponseCode.OK, Message: '' };
        state.statsData = { Items: [], ResponseCode: AgmsResponseCode.OK, Message: '' };
        state.error = action.error;
      });
  },
});

export const {
  setShouldViewPerCurrency,
  setShouldExpandSum,
  clearCurrencyData,
  setSelectedCurrency,
  resetTableFooter,
  setViewPerCcyDialogHeight,
  applySearchInput,
} = statsSlice.actions;

export default statsSlice.reducer;
